import { IGif, INicheMap, IUser } from "services/types";

import { TopicSelector } from "./TopicSelector";
import { FeedControl } from "./FeedControl";
import { TileFeed } from "./TileFeed";
import { PreviewFeed } from "./PreviewFeed";
import { FilterByTag } from "components/UI/FilterByTag";
import { ViewRegion } from "utils/metrics";

import "./GifList.css";
import React, { Fragment, ReactNode, useCallback } from "react";
import { FeedAds } from "components/ads/FeedAds";
import { MobileContentPageScrollKeys } from "components/ads/config/units/contentPageScrollMobile";
import { DesktopContentPageScrollKeys } from "components/ads/config/units/contentPageScrollDesktop";

const TOPICS = {
  "a": "All",
  "g": "GIFs",
  "i": "Images",
};

export interface IUserList {
  [key: string]: IUser;
}

interface IProps {
  ordering?: boolean;
  orderingTop?: boolean;
  topics?: boolean;
  manage?: boolean;

  topic: string;
  order: string;
  tiles: boolean;
  verified?: boolean;
  gifs: IGif[];
  users: IUserList;
  selectedTags?: string[];
  niches?: INicheMap;
  totalLength: number;
  hasMore: boolean;
  changeTopic: (topic: string) => void;
  changeOrder: (order: string) => void;
  changeMode: (tiles: boolean) => void;
  // TODO:
  changeTags?: any;
  changeVerified?: (verified: boolean) => void;
  fetchMore: () => void;
  userID?: string;
  isError?: boolean;
  isLoading?: boolean;
  className?: string;
  injector?: (pos: number, gif: IGif, fullScreen: boolean) => JSX.Element;
  onSelect?: (ids: string[]) => void;
  source: ViewRegion;
  isVerifyHidden?: boolean;

  // Passed to the watch page to know where the user comes from.
  rel?: string;
}

export const GifList = (props: IProps) => {
  const { injector: parentInjector } = props;

  const injector = useCallback((pos: number, gif: IGif, fullscreen: boolean): JSX.Element => {
    const children: ReactNode[] = [];

    if (parentInjector) {
      parentInjector(pos, gif, fullscreen);
    }

    children.push(
      <FeedAds
        key={`feedAds${gif.id}${pos}`}
        position={pos}
        fullscreen={fullscreen}
        mobileAds={MobileContentPageScrollKeys}
        desktopAds={DesktopContentPageScrollKeys}
      />
    );

    return <Fragment>{children}</Fragment>;
  }, [parentInjector]);

  return (
    <div className={`gifList ${props.className ? props.className : ""}`}>
      {props.selectedTags && <FilterByTag
        userId={props.userID!}
        changeTags={props.changeTags}
        selectedTags={props.selectedTags}
        topic={props.topic}
      />}

      {props.topics !== false && <TopicSelector
        topics={TOPICS}
        active={props.topic}
        changeTopic={props.changeTopic}
      />}

      <FeedControl
        order={props.order}
        ordering={props.ordering}
        orderingTop={props.orderingTop}
        tiles={props.tiles}
        verified={!!props.verified}
        changeOrder={props.changeOrder}
        changeMode={props.changeMode}
        changeVerified={props.changeVerified}
      />

      {props.isError && <p className="errorMessage">Error loading gifs, please refresh.</p>}

      {props.tiles && <TileFeed
        gifs={props.gifs}
        hasMore={props.hasMore}
        fetchMore={props.fetchMore}
        manage={!!props.manage}
        onSelect={props.onSelect}
        injector={props.injector}
        source={props.source}
        rel={props.rel}
        order={props.order}
        verified={!!props.verified}
        isLoading={props.isLoading}
        isVerifyHidden={props.isVerifyHidden}
      />}

      {!props.tiles && <PreviewFeed
        gifs={props.gifs}
        users={props.users}
        niches={props.niches}
        hasMore={props.hasMore}
        fetchMore={props.fetchMore}
        injector={injector}
        rel={props.rel}
        source={props.source}
        isLoading={props.isLoading}
      />}
    </div>
  );
};
